import React from "react";
import "./style/bilan.css";
import useBilanData from "../hooks/useBilanData";
import Loader from "components/Loader/Loader";

const Bilan = ({dossier_id, exercice_id = null}) => {

  const {
    bilanData,
    lignesActif,
    lignesPassif,
    totaux,
    isLoading,
    error,
    refetch,
  } = useBilanData(dossier_id, exercice_id);

  // Fonction pour formater les montants
  const formatMontant = (montant) => {
    if (montant === null || montant === undefined || isNaN(montant)) return '0';
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(montant);
  };

  // Message si aucun exercice_id fourni
  if (!exercice_id) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '60px 20px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        border: '2px dashed #dee2e6'
      }}>
        <i className="fas fa-hand-pointer" style={{fontSize: '48px', color: '#6c757d', marginBottom: '15px'}}></i>
        <p style={{fontSize: '18px', color: '#6c757d', marginBottom: '10px', fontWeight: '500'}}>
          Veuillez fournir un exercice_id
        </p>
      </div>
    );
  }

  return (
    <div className="bilan-container" style={{ marginLeft: "20px" }}>
      {/* Affichage conditionnel selon l'état */}
      {isLoading && (
        <div style={{textAlign: 'center', padding: '50px'}}>
          <Loader />
        </div>
      )}

      {error && (
        <div style={{textAlign: 'center', padding: '50px'}}>
          <div style={{
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '8px',
            padding: '30px',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            <i className="fas fa-exclamation-triangle" style={{fontSize: '48px', color: '#856404', marginBottom: '15px'}}></i>
            <h3 style={{color: '#856404', marginBottom: '15px'}}>Bilan non disponible</h3>
            <p style={{color: '#856404', marginBottom: '15px', fontSize: '16px'}}>
              Le bilan comptable n&apos;a pas encore été généré pour cet exercice.
            </p>
            <button onClick={() => refetch()} className="btn btn-warning mt-2">
              <i className="fas fa-redo"></i> Réessayer
            </button>
          </div>
        </div>
      )}

      {!isLoading && !error && !bilanData && (
        <div style={{textAlign: 'center', padding: '50px'}}>
          <div style={{fontSize: '18px', color: '#666', marginBottom: '20px'}}>
            <i className="fas fa-file-invoice" style={{fontSize: '48px', color: '#ccc', marginBottom: '15px'}}></i>
            <p style={{fontWeight: 'bold', fontSize: '20px', marginBottom: '10px'}}>Aucun bilan disponible</p>
            <p>Aucun bilan comptable n&apos;a été généré pour cet exercice.</p>
          </div>
          <button onClick={() => refetch()} className="btn btn-outline-primary mt-3">
            <i className="fas fa-sync-alt"></i> Actualiser
          </button>
        </div>
      )}

      {!isLoading && bilanData && (!lignesActif || lignesActif.length === 0) && (!lignesPassif || lignesPassif.length === 0) && (
        <div style={{textAlign: 'center', padding: '50px'}}>
          <div style={{fontSize: '18px', color: '#666', marginBottom: '20px'}}>
            <i className="fas fa-inbox" style={{fontSize: '48px', color: '#ccc', marginBottom: '15px'}}></i>
            <p style={{fontWeight: 'bold', fontSize: '20px', marginBottom: '10px'}}>Bilan vide</p>
            <p>Le bilan existe mais ne contient aucune donnée.</p>
            <p style={{fontSize: '14px', color: '#999', marginTop: '10px'}}>Veuillez ajouter des écritures comptables puis régénérer le bilan.</p>
          </div>
          <button onClick={() => refetch()} className="btn btn-outline-primary mt-3">
            <i className="fas fa-sync-alt"></i> Actualiser
          </button>
        </div>
      )}

      {/* Affichage du bilan seulement si les données sont valides */}
      {!isLoading && !error && bilanData && (
        <>
          {/* Informations de mise à jour */}
          {bilanData?.date_modification && (
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: '10px',
          fontSize: '13px',
          color: '#666'
        }}>
          <i className="fas fa-clock" style={{marginRight: '5px'}}></i>
          Dernière mise à jour: {new Date(bilanData.date_modification).toLocaleString('fr-FR')}
        </div>
      )}

      {/* Alerte si le bilan est vide (tous les totaux sont à zéro) */}
      {bilanData && totaux.actif_total === 0 && totaux.passif_total === 0 && (lignesActif.length > 0 || lignesPassif.length > 0) && (
        <div style={{
          backgroundColor: '#fff3cd',
          border: '1px solid #ffc107',
          borderRadius: '8px',
          padding: '15px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <i className="fas fa-exclamation-triangle" style={{color: '#856404', fontSize: '24px'}}></i>
          <div>
            <strong style={{color: '#856404'}}>Attention :</strong>
            <span style={{color: '#856404', marginLeft: '8px'}}>
              Le bilan ne contient aucune valeur. Veuillez vérifier les écritures comptables et régénérer le bilan.
            </span>
          </div>
        </div>
      )}

      {/* Alerte si le bilan est déséquilibré */}
      {bilanData && totaux.actif_total !== totaux.passif_total && totaux.actif_total !== 0 && totaux.passif_total !== 0 && (
        <div style={{
          backgroundColor: '#f8d7da',
          border: '1px solid #dc3545',
          borderRadius: '8px',
          padding: '15px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <i className="fas fa-times-circle" style={{color: '#721c24', fontSize: '24px'}}></i>
          <div>
            <strong style={{color: '#721c24'}}>Erreur de bilan :</strong>
            <span style={{color: '#721c24', marginLeft: '8px'}}>
              Le bilan est déséquilibré. Actif: {formatMontant(totaux.actif_total)} ≠ Passif: {formatMontant(totaux.passif_total)}
            </span>
            <div style={{fontSize: '13px', color: '#721c24', marginTop: '5px'}}>
              Écart: {formatMontant(Math.abs(totaux.actif_total - totaux.passif_total))}
            </div>
          </div>
        </div>
      )}

      {/* Indicateur de succès si le bilan est équilibré */}
      {bilanData && totaux.actif_total === totaux.passif_total && (totaux.actif_total > 0 || (lignesActif.length > 0 || lignesPassif.length > 0)) && (
        <div style={{
          backgroundColor: '#d4edda',
          border: '1px solid #28a745',
          borderRadius: '8px',
          padding: '12px 15px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <i className="fas fa-check-circle" style={{color: '#155724', fontSize: '20px'}}></i>
          <div>
            <strong style={{color: '#155724'}}>Bilan équilibré :</strong>
            <span style={{color: '#155724', marginLeft: '8px'}}>
              Actif = Passif = {formatMontant(totaux.actif_total)}
            </span>
          </div>
        </div>
      )}

      <div className="bilan-header">
        <div className="header-left">
          <div className="header-row">
            <span className="header-label">Désignation entité:.................................................................</span>
          </div>
          <div className="header-row">
            <span className="header-label">Numéro d&apos;identification :......................................</span>
          </div>
        </div>
        <div className="header-center">
          <h2 className="bilan-title">BILAN AU 31 DÉCEMBRE N</h2>
        </div>
        <div className="header-right">
          <div className="header-row">
            <span className="header-label">Exercice clos le 31-12-........................</span>
          </div>
          <div className="header-row">
            <span className="header-label">Durée (en mois)........................</span>
          </div>
        </div>
      </div>

      <div className="bilan-table-wrapper">
        <table className="bilan-table">
          <thead>
            <tr className="header-row-group">
              <th rowSpan="2" className="col-ref-header">REF</th>
              <th rowSpan="2" className="col-label-header">ACTIF</th>
              <th rowSpan="2" className="col-note-header">Note</th>
              <th colSpan="2" className="header-group-main">EXERCICE au 31/12/ N</th>
              <th colSpan="2" className="header-group-main">EXERCICE<br/>AU<br/>31/12/N-1</th>
              <th rowSpan="2" className="col-ref-header separator-left">REF</th>
              <th rowSpan="2" className="col-label-header">PASSIF</th>
              <th rowSpan="2" className="col-note-header">Note</th>
              <th rowSpan="2" className="header-group-main">EXERCICE<br/>AU<br/>31/12/N<br/><br/>NET</th>
            </tr>
            <tr className="header-row-main">
              <th className="col-brut-header">BRUT</th>
              <th className="col-amort-header">AMORT et<br/>DEPREC.</th>
              <th className="col-net-header">NET</th>
              <th className="col-net-header">NET</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="col-ref">AD</td>
              <td className="col-label section-title">IMMOBILISATIONS INCORPORELLES</td>
              <td className="col-note">3</td>
              <td className="col-brut"></td>
              <td className="col-amort"></td>
              <td className="col-net"></td>
              <td className="col-net-n1"></td>
              <td className="col-ref-passif">CA</td>
              <td className="col-label-passif">Capital</td>
              <td className="col-note-p"></td>
              <td className="col-net-p">12</td>
            </tr>
            <tr>
              <td className="col-ref">AE</td>
              <td className="col-label">Frais de développement et de prospection</td>
              <td className="col-note"></td>
              <td className="col-brut"></td>
              <td className="col-amort"></td>
              <td className="col-net"></td>
              <td className="col-net-n1"></td>
              <td className="col-ref-passif">CB</td>
              <td className="col-label-passif">Apporteurs capital non appelé <span style={{marginLeft: '50px'}}>(-)</span></td>
              <td className="col-note-p"></td>
              <td className="col-net-p">13</td>
            </tr>
            <tr>
              <td className="col-ref">AF</td>
              <td className="col-label">Brevets, licences, logiciels et droits similaires</td>
              <td className="col-note"></td>
              <td className="col-brut"></td>
              <td className="col-amort"></td>
              <td className="col-net"></td>
              <td className="col-net-n1"></td>
              <td className="col-ref-passif">CD</td>
              <td className="col-label-passif">Primes liées au capital social</td>
              <td className="col-note-p"></td>
              <td className="col-net-p">14</td>
            </tr>
            <tr>
              <td className="col-ref">AG</td>
              <td className="col-label">Fonds commercial et droit au bail</td>
              <td className="col-note"></td>
              <td className="col-brut"></td>
              <td className="col-amort"></td>
              <td className="col-net"></td>
              <td className="col-net-n1"></td>
              <td className="col-ref-passif">CE</td>
              <td className="col-label-passif">Écarts de réévaluation</td>
              <td className="col-note-p"></td>
              <td className="col-net-p">3e</td>
            </tr>
            <tr>
              <td className="col-ref">AH</td>
              <td className="col-label">Autres immobilisations incorporelles</td>
              <td className="col-note"></td>
              <td className="col-brut"></td>
              <td className="col-amort"></td>
              <td className="col-net"></td>
              <td className="col-net-n1"></td>
              <td className="col-ref-passif">CF</td>
              <td className="col-label-passif">Réserves indisponibles</td>
              <td className="col-note-p"></td>
              <td className="col-net-p">14</td>
            </tr>
            <tr>
              <td className="col-ref">AI</td>
              <td className="col-label section-title total-label">IMMOBILISATIONS CORPORELLES</td>
              <td className="col-note">3</td>
              <td className="col-brut"></td>
              <td className="col-amort"></td>
              <td className="col-net"></td>
              <td className="col-net-n1"></td>
              <td className="col-ref-passif">CG</td>
              <td className="col-label-passif">Réserves libres</td>
              <td className="col-note-p"></td>
              <td className="col-net-p">14</td>
            </tr>
            <tr>
              <td className="col-ref">AJ</td>
              <td className="col-label">Terrains (1) <br /> <i>(1)dont Placementen en Net............../................</i></td>
              <td className="col-note"></td>
              <td className="col-brut"></td>
              <td className="col-amort"></td>
              <td className="col-net"></td>
              <td className="col-net-n1"></td>
              <td className="col-ref-passif">CH</td>
              <td className="col-label-passif">Report à nouveau <span style={{marginLeft: '30px'}}>(+ ou -)</span></td>
              <td className="col-note-p">14</td>
              <td className="col-net-p"></td>
            </tr>
            <tr>
              <td className="col-ref">AK</td>
              <td className="col-label">Bâtiments <br /> <i>(1)dont Placementen en Net............../................</i></td>
              <td className="col-note"></td>
              <td className="col-brut"></td>
              <td className="col-amort"></td>
              <td className="col-net"></td>
              <td className="col-net-n1"></td>
              <td className="col-ref-passif">CJ</td>
              <td className="col-label-passif">Résultat net de l&apos;exercice (bénéfice + ou perte -)</td>
              <td className="col-note-p"></td>
              <td className="col-net-p"></td>
            </tr>
            <tr>
              <td className="col-ref">AL</td>
              <td className="col-label">Aménagements, agencements et installations</td>
              <td className="col-note"></td>
              <td className="col-brut"></td>
              <td className="col-amort"></td>
              <td className="col-net"></td>
              <td className="col-net-n1"></td>
              <td className="col-ref-passif">CL</td>
              <td className="col-label-passif">Subventions d&apos;investissement</td>
              <td className="col-note-p"></td>
              <td className="col-net-p">15</td>
            </tr>
            <tr>
              <td className="col-ref">AM</td>
              <td className="col-label">Matériel, mobilier et actifs biologiques</td>
              <td className="col-note"></td>
              <td className="col-brut"></td>
              <td className="col-amort"></td>
              <td className="col-net"></td>
              <td className="col-net-n1"></td>
              <td className="col-ref-passif">CM</td>
              <td className="col-label-passif">Provisions réglementaires</td>
              <td className="col-note-p"></td>
              <td className="col-net-p">15</td>
            </tr>
            <tr>
              <td className="col-ref">AN</td>
              <td className="col-label">Matériel de transport</td>
              <td className="col-note"></td>
              <td className="col-brut"></td>
              <td className="col-amort"></td>
              <td className="col-net"></td>
              <td className="col-net-n1"></td>
              <td className="col-ref-passif">CP</td>
              <td className="col-label-passif section-title">TOTAL CAPITAUX PROPRES ET RESSOURCES ASSIMILEES</td>
              <td className="col-note-p"></td>
              <td className="col-net-p"></td>
            </tr>
            <tr>
              <td className="col-ref">AP</td>
              <td className="col-label">AVANCES ET ACOMPTES VERSES SUR IMMOBILISATIONS</td>
              <td className="col-note">3</td>
              <td className="col-brut"></td>
              <td className="col-amort"></td>
              <td className="col-net"></td>
              <td className="col-net-n1"></td>
              <td className="col-ref-passif">DA</td>
              <td className="col-label-passif">Emprunts et dettes financières diverses</td>
              <td className="col-note-p"></td>
              <td className="col-net-p">16</td>
            </tr>
            <tr>
              <td className="col-ref">AQ</td>
              <td className="col-label">IMMOBILISATIONS FINANCIERES</td>
              <td className="col-note">4</td>
              <td className="col-brut"></td>
              <td className="col-amort"></td>
              <td className="col-net"></td>
              <td className="col-net-n1"></td>
              <td className="col-ref-passif">DB</td>
              <td className="col-label-passif">Dettes de location acquisition</td>
              <td className="col-note-p"></td>
              <td className="col-net-p">16</td>
            </tr>
            <tr>
              <td className="col-ref">AR</td>
              <td className="col-label">Titres de participation</td>
              <td className="col-note"></td>
              <td className="col-brut"></td>
              <td className="col-amort"></td>
              <td className="col-net"></td>
              <td className="col-net-n1"></td>
              <td className="col-ref-passif">BC</td>
              <td className="col-label-passif">Provisions pour risques et charges</td>
              <td className="col-note-p"></td>
              <td className="col-net-p">16</td>
            </tr>
            <tr>
              <td className="col-ref">AS</td>
              <td className="col-label">Autres immobilisations financières</td>
              <td className="col-note"></td>
              <td className="col-brut"></td>
              <td className="col-amort"></td>
              <td className="col-net"></td>
              <td className="col-net-n1"></td>
              <td className="col-ref-passif">DD</td>
              <td className="col-label-passif section-title">TOTAL DETTES FINANCIERES ET RESSOURCES ASSIMILEES</td>
              <td className="col-note-p"></td>
              <td className="col-net-p"></td>
            </tr>
            <tr className="total-row">
              <td className="col-ref">AZ</td>
              <td className="col-label">TOTAL ACTIF IMMOBILISE</td>
              <td className="col-note"></td>
              <td className="col-brut"></td>
              <td className="col-amort"></td>
              <td className="col-net"></td>
              <td className="col-net-n1"></td>
              <td className="col-ref-passif">DE</td>
              <td className="col-label-passif">TOTAL RESSOURCES STABLES</td>
              <td className="col-note-p"></td>
              <td className="col-net-p"></td>
            </tr>
            <tr>
              <td className="col-ref">BA</td>
              <td className="col-label">ACTIF CIRCULANT HAO</td>
              <td className="col-note">5</td>
              <td className="col-brut"></td>
              <td className="col-amort"></td>
              <td className="col-net"></td>
              <td className="col-net-n1"></td>
              <td className="col-ref-passif">DH</td>
              <td className="col-label-passif">Dettes circulantes HAO</td>
              <td className="col-note-p"></td>
              <td className="col-net-p">5</td>
            </tr>
            <tr>
              <td className="col-ref">BB</td>
              <td className="col-label">STOCKS ET ENCOURS</td>
              <td className="col-note">6</td>
              <td className="col-brut"></td>
              <td className="col-amort"></td>
              <td className="col-net"></td>
              <td className="col-net-n1"></td>
              <td className="col-ref-passif">DI</td>
              <td className="col-label-passif">Clients, avances reçues</td>
              <td className="col-note-p">7</td>
              <td className="col-net-p"></td>
            </tr>
            <tr>
              <td className="col-ref">BG</td>
              <td className="col-label">CREANCES ET EMPLOIS ASSIMILES</td>
              <td className="col-note"></td>
              <td className="col-brut"></td>
              <td className="col-amort"></td>
              <td className="col-net"></td>
              <td className="col-net-n1"></td>
              <td className="col-ref-passif">DJ</td>
              <td className="col-label-passif">Fournisseurs d&apos;exploitation</td>
              <td className="col-note-p"></td>
              <td className="col-net-p">17</td>
            </tr>
            <tr>
              <td className="col-ref">BH</td>
              <td className="col-label">Fournisseurs avances versées</td>
              <td className="col-note">17</td>
              <td className="col-brut"></td>
              <td className="col-amort"></td>
              <td className="col-net"></td>
              <td className="col-net-n1"></td>
              <td className="col-ref-passif">DK</td>
              <td className="col-label-passif">Dettes fiscales et sociales</td>
              <td className="col-note-p"></td>
              <td className="col-net-p">18</td>
            </tr>
            <tr>
              <td className="col-ref">BI</td>
              <td className="col-label">Clients</td>
              <td className="col-note">7</td>
              <td className="col-brut"></td>
              <td className="col-amort"></td>
              <td className="col-net"></td>
              <td className="col-net-n1"></td>
              <td className="col-ref-passif">DM</td>
              <td className="col-label-passif">Autres dettes</td>
              <td className="col-note-p">19</td>
              <td className="col-net-p"></td>
            </tr>
            <tr>
              <td className="col-ref">BJ</td>
              <td className="col-label">Autres créances</td>
              <td className="col-note">8</td>
              <td className="col-brut"></td>
              <td className="col-amort"></td>
              <td className="col-net"></td>
              <td className="col-net-n1"></td>
              <td className="col-ref-passif">DN</td>
              <td className="col-label-passif">Provisions pour risques à court terme</td>
              <td className="col-note-p">19</td>
              <td className="col-net-p"></td>
            </tr>
            <tr className="total-row">
              <td className="col-ref">BK</td>
              <td className="col-label">TOTAL ACTIF CIRCULANT</td>
              <td className="col-note"></td>
              <td className="col-brut"></td>
              <td className="col-amort"></td>
              <td className="col-net"></td>
              <td className="col-net-n1"></td>
              <td className="col-ref-passif">DP</td>
              <td className="col-label-passif">TOTAL PASSIF CIRCULANT</td>
              <td className="col-note-p"></td>
              <td className="col-net-p"></td>
            </tr>
            <tr>
              <td className="col-ref">BQ</td>
              <td className="col-label">Titres de placement</td>
              <td className="col-note">9</td>
              <td className="col-brut"></td>
              <td className="col-amort"></td>
              <td className="col-net"></td>
              <td className="col-net-n1"></td>
              <td className="col-ref-passif">DQ</td>
              <td className="col-label-passif">Banques, crédits d&apos;escompte</td>
              <td className="col-note-p"></td>
              <td className="col-net-p">20</td>
            </tr>
            <tr>
              <td className="col-ref">BR</td>
              <td className="col-label">Valeurs à réaliser</td>
              <td className="col-note">10</td>
              <td className="col-brut"></td>
              <td className="col-amort"></td>
              <td className="col-net"></td>
              <td className="col-net-n1"></td>
              <td className="col-ref-passif">DR</td>
              <td className="col-label-passif">Banques, établissements financiers et crédits de trésorier</td>
              <td className="col-note-p"></td>
              <td className="col-net-p">20</td>
            </tr>
            <tr>
              <td className="col-ref">BS</td>
              <td className="col-label">Banques, chèques postaux, caisse et assimilés</td>
              <td className="col-note">11</td>
              <td className="col-brut"></td>
              <td className="col-amort"></td>
              <td className="col-net"></td>
              <td className="col-net-n1"></td>
              <td className="col-ref-passif">DR</td>
              <td className="col-label-passif">Banques, établissements financiers et crédits de trésorerie</td>
              <td className="col-note-p">20</td>
              <td className="col-net-p">20</td>
            </tr>
            <tr className="total-row">
              <td className="col-ref">BT</td>
              <td className="col-label">TOTAL TRESORERIE-ACTIF</td>
              <td className="col-note"></td>
              <td className="col-brut"></td>
              <td className="col-amort"></td>
              <td className="col-net"></td>
              <td className="col-net-n1"></td>
              <td className="col-ref-passif">DT</td>
              <td className="col-label-passif">TOTAL TRESORERIE-PASSIF</td>
              <td className="col-note-p"></td>
              <td className="col-net-p"></td>
            </tr>
            <tr>
              <td className="col-ref">BU</td>
              <td className="col-label">Écart de conversion-Actif</td>
              <td className="col-note">12</td>
              <td className="col-brut"></td>
              <td className="col-amort"></td>
              <td className="col-net"></td>
              <td className="col-net-n1"></td>
              <td className="col-ref-passif">DV</td>
              <td className="col-label-passif">Écart de conversion-Passif</td>
              <td className="col-note-p">12</td>
              <td className="col-net-p"></td>
            </tr>
            <tr className="grand-total-row">
              <td className="col-ref">BZ</td>
              <td className="col-label">TOTAL GENERAL</td>
              <td className="col-note"></td>
              <td className="col-brut"></td>
              <td className="col-amort"></td>
              <td className="col-net"></td>
              <td className="col-net-n1"></td>
              <td className="col-ref-passif">DZ</td>
              <td className="col-label-passif">TOTAL GENERAL</td>
              <td className="col-note-p"></td>
              <td className="col-net-p"></td>
            </tr>
          </tbody>
        </table>
      </div>
        </>
      )}
    </div>
  );
};

export default Bilan;
